import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Mail, Phone, Download } from 'lucide-react';
import Header from '../components/layout/Header';
import Badge from '../components/common/Badge';
import VoiceInput from '../components/common/VoiceInput';
import AddContactModal from '../components/AddContactModal';
import { getContacts, downloadExport } from '../services/api';
import type { Contact } from '../types';

const statusVariant: Record<string, string> = {
  customer: 'success',
  prospect: 'info',
  lead: 'sage',
  active: 'success',
  inactive: 'default',
};

function ScoreBar({ score }: { score: number }) {
  const color = score >= 70 ? 'var(--success)' : score >= 40 ? 'var(--warning)' : 'var(--error)';
  return (
    <div className="flex items-center gap-2">
      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
      <div className="w-16 h-1.5 rounded-full" style={{ background: 'var(--bg-input)' }}>
        <div className="h-full rounded-full" style={{ width: `${Math.min(score, 100)}%`, background: color }} />
      </div>
      <span className="text-xs" style={{ color }}>{score}</span>
    </div>
  );
}

export default function ContactsPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const PAGE_SIZE = 25;

  const mapContact = (c: any): Contact => ({
    id: c.id,
    first_name: c.first_name || '',
    last_name: c.last_name || '',
    email: c.email || '',
    phone: c.phone,
    company: c.organization_name || c.company || '',
    title: c.title || '',
    lead_score: c.lead_score ?? c.relationship_strength ? Math.round((c.relationship_strength || 0) * 100) : 0,
    status: c.status || 'active',
    created_at: c.created_at || '',
    updated_at: c.updated_at || '',
  });

  const loadContacts = useCallback(async (loadPage = 1, append = false) => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        size: String(PAGE_SIZE),
        page: String(loadPage),
        sort_by: 'last_name',
        sort_order: 'asc',
      };
      if (search.trim()) params.query = search.trim();
      const data = await getContacts(params);
      const items = data?.items ?? data ?? [];
      const mapped = Array.isArray(items) ? items.map(mapContact) : [];
      setContacts((prev) => append ? [...prev, ...mapped] : mapped);
      const totalCount = data?.total ?? items.length ?? 0;
      setTotal(totalCount);
      setPage(loadPage);
      setHasMore(loadPage * PAGE_SIZE < totalCount);
    } catch (err) {
      console.error('Failed to load contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const debounce = setTimeout(() => loadContacts(1, false), 150);
    return () => clearTimeout(debounce);
  }, [loadContacts]);

  const filtered = contacts;

  return (
    <div className="page-enter">
      <Header title="Contacts" subtitle={`${total} contacts`} />

      <div className="p-4 md:p-8">
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6">
          <div className="relative w-full md:w-auto">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 z-10"
              style={{ color: 'var(--text-muted)' }} />
            <VoiceInput
              value={search}
              onChange={setSearch}
              placeholder="Search contacts..."
              className="pl-10 min-h-[48px] rounded-lg text-sm w-full md:w-[320px] outline-none"
              style={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border-primary)',
                color: 'var(--text-primary)',
              }}
            />
          </div>
          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 min-h-[48px] rounded-lg text-sm cursor-pointer transition-all"
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}>
              <Filter size={16} />
              Filter
            </button>
            <button
              onClick={() => downloadExport('contacts')}
              className="flex items-center gap-2 px-4 min-h-[48px] rounded-lg text-sm cursor-pointer transition-all"
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}>
              <Download size={16} />
              Export CSV
            </button>
            <button
              onClick={() => setShowAddContact(true)}
              className="flex items-center gap-2 px-4 min-h-[48px] rounded-lg text-sm font-body cursor-pointer transition-all"
              style={{ background: 'var(--netrun-green)', border: 'none', color: 'var(--netrun-black)' }}>
              <Plus size={16} />
              Add Contact
            </button>
          </div>
        </div>

        {loading && (
          <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>Loading contacts...</p>
        )}

        {!loading && filtered.length === 0 && (
          <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>
            No contacts found. Add your first contact to get started.
          </p>
        )}

        {/* Desktop: row layout (hidden on mobile) */}
        <div className="hidden md:grid grid-cols-1 gap-3">
          {filtered.map((contact, idx) => (
            <div
              key={contact.id}
              onClick={() => navigate(`/contacts/${contact.id}`)}
              className="flex items-center justify-between px-6 py-4 rounded-xl cursor-pointer card-interactive"
              style={{
                background: idx % 2 === 0 ? 'var(--bg-panel)' : 'rgba(255,255,255,0.02)',
                border: '1px solid var(--border-primary)',
              }}
            >
              {/* Avatar + Name */}
              <div className="flex items-center gap-4 w-[280px]">
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-display text-sm"
                  style={{ background: 'var(--bg-hover)', color: 'var(--netrun-green)' }}>
                  {(contact.first_name?.[0] || contact.email?.[0] || '?')}{(contact.last_name?.[0] || '')}
                </div>
                <div>
                  <p className="text-sm font-body" style={{ color: 'var(--text-primary)' }}>
                    {(contact.first_name || contact.last_name)
                      ? `${contact.first_name} ${contact.last_name}`.trim()
                      : contact.email || 'Unknown'}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {contact.title}
                  </p>
                </div>
              </div>

              {/* Company */}
              <div className="w-[160px]">
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{contact.company}</p>
              </div>

              {/* Lead Score */}
              <div className="w-[120px]">
                <ScoreBar score={contact.lead_score || 0} />
              </div>

              {/* Status */}
              <div className="w-[100px]">
                <Badge label={contact.status} variant={statusVariant[contact.status] as any} />
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button className="min-w-[48px] min-h-[48px] flex items-center justify-center rounded-lg transition-all cursor-pointer"
                  style={{ color: 'var(--text-muted)', background: 'transparent', border: 'none' }}
                  title="Send email">
                  <Mail size={16} />
                </button>
                <button className="min-w-[48px] min-h-[48px] flex items-center justify-center rounded-lg transition-all cursor-pointer"
                  style={{ color: 'var(--text-muted)', background: 'transparent', border: 'none' }}
                  title="Call">
                  <Phone size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: card layout (visible only on < md) */}
        <div className="md:hidden grid grid-cols-1 gap-3">
          {filtered.map((contact) => (
            <div
              key={contact.id}
              onClick={() => navigate(`/contacts/${contact.id}`)}
              className="rounded-xl p-4 cursor-pointer transition-all duration-200 touch-manipulation card-interactive"
              style={{
                background: 'var(--bg-panel)',
                border: '1px solid var(--border-primary)',
              }}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-display text-sm flex-shrink-0"
                  style={{ background: 'var(--bg-hover)', color: 'var(--netrun-green)' }}>
                  {(contact.first_name?.[0] || contact.email?.[0] || '?')}{(contact.last_name?.[0] || '')}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-body truncate" style={{ color: 'var(--text-primary)' }}>
                    {(contact.first_name || contact.last_name)
                      ? `${contact.first_name} ${contact.last_name}`.trim()
                      : contact.email || 'Unknown'}
                  </p>
                  {contact.company && (
                    <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                      {contact.company}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                    {contact.email && (
                      <a href={`mailto:${contact.email}`} className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                        {contact.email}
                      </a>
                    )}
                    {contact.phone && (
                      <a href={`tel:${contact.phone}`} className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {contact.phone}
                      </a>
                    )}
                  </div>
                </div>
                <Badge label={contact.status} variant={statusVariant[contact.status] as any} />
              </div>
            </div>
          ))}
        </div>

        {/* Load More */}
        {hasMore && !loading && (
          <div className="flex justify-center mt-6">
            <button
              onClick={() => loadContacts(page + 1, true)}
              className="px-6 py-3 rounded-lg text-sm font-body cursor-pointer transition-all"
              style={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-secondary)',
              }}
            >
              Load More ({total - page * PAGE_SIZE} remaining)
            </button>
          </div>
        )}
      </div>

      {/* Add Contact Modal */}
      <AddContactModal
        open={showAddContact}
        onClose={() => setShowAddContact(false)}
        onSuccess={() => {
          setShowAddContact(false);
          loadContacts();
        }}
      />
    </div>
  );
}
